import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  createChatReaction,
  deleteChatMessage,
  deleteChatReaction,
} from "../../../api/api";
import { cn } from "../../../lib/utils";
import EmojiPickerModal from "./EmojiPickerModal";

const commonReactions = ["👍", "❤️", "😊", "😂", "😮", "😢", "😡"];

/**
 * Format a message timestamp for the more-actions menu header.
 * Today: time only. Within the past week: weekday + time. Older: full date + time.
 * @param {string|number|Date} [timestamp]
 * @returns {string}
 */
function formatMenuTimestamp(timestamp) {
  if (timestamp == null || timestamp === "") return "";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return time;
  }

  const weekMs = 7 * 24 * 60 * 60 * 1000;
  if (now.getTime() - date.getTime() >= weekMs) {
    const fullDate = date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${fullDate}, ${time}`;
  }

  const weekday = date.toLocaleDateString([], { weekday: "short" });
  return `${weekday} ${time}`;
}

/**
 * Minimal outline icon: smile / react.
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconSmile({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <circle cx="9" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Minimal outline icon: reply arrow.
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconReply({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 14L4 9l5-5" />
      <path d="M20 19v-2a7 7 0 0 0-7-7H4" />
    </svg>
  );
}

/**
 * Minimal outline icon: more (vertical dots).
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconMoreVertical({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

const iconButtonClass =
  "inline-flex shrink-0 items-center justify-center text-icon-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring disabled:pointer-events-none disabled:opacity-40";

const iconButtonBesideClass =
  "h-8 w-8 rounded-md hover:bg-highlight hover:text-text-primary focus-visible:ring-offset-1 focus-visible:ring-offset-primary";

const iconButtonOverlayClass =
  "h-6 w-6 rounded-full hover:bg-highlight/40";

const overlayIconClass = "h-3.5 w-3.5";

/** Space needed beside the bubble for the 3 icon buttons + gaps + padding. */
const ACTION_BAR_MIN_SPACE_PX = 112;

/**
 * Visible horizontal bounds of the nearest overflow-x clipping ancestor.
 * @param {HTMLElement} el
 * @returns {{ left: number, right: number, node: HTMLElement|null }}
 */
function getHorizontalClipRect(el) {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowX = style.overflowX;
    if (
      overflowX === "auto" ||
      overflowX === "scroll" ||
      overflowX === "overlay" ||
      overflowX === "hidden"
    ) {
      const rect = node.getBoundingClientRect();
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const left = rect.left + node.clientLeft + padLeft;
      const right = rect.left + node.clientLeft + node.clientWidth - padRight;
      return { left, right, node };
    }
    node = node.parentElement;
  }
  return { left: 0, right: window.innerWidth, node: null };
}

/**
 * Hover actions beside the message bubble, or overlaid on it when the row is too narrow.
 * @param {Object} props
 * @param {Object} props.message - Message object.
 * @param {boolean} props.isOwnMessage - Whether this is the user's own message.
 * @param {number} props.userId - Current user id.
 * @param {number} props.chatId - Chat id for reaction API.
 * @param {(messageId: number) => void} [props.onMessageDeleted]
 * @param {(message: Object) => void} [props.onReply]
 * @returns {JSX.Element|null}
 */
function MessageActions({
  message,
  isOwnMessage,
  userId,
  chatId,
  onMessageDeleted,
  onReply,
}) {
  const [isReactionPickerOpen, setIsReactionPickerOpen] = useState(false);
  const [isEmojiModalOpen, setIsEmojiModalOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [openMoreAbove, setOpenMoreAbove] = useState(false);
  const [overlayOnBubble, setOverlayOnBubble] = useState(false);
  const moreWrapRef = useRef(null);
  const moreMenuRef = useRef(null);
  const moreButtonRef = useRef(null);
  const pickerRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    if (!isMoreOpen && !isReactionPickerOpen) return;
    function handlePointerDown(event) {
      if (
        moreWrapRef.current &&
        !moreWrapRef.current.contains(event.target) &&
        isMoreOpen
      ) {
        setIsMoreOpen(false);
      }
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        isReactionPickerOpen
      ) {
        setIsReactionPickerOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isMoreOpen, isReactionPickerOpen]);

  /**
   * Bottom edge of the nearest scroll/clipping ancestor (or the viewport).
   * @param {HTMLElement} el
   * @returns {number}
   */
  function getVisibleBottom(el) {
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if (
        (overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowY === "overlay" ||
          overflowY === "hidden") &&
        node.clientHeight > 0
      ) {
        return node.getBoundingClientRect().bottom;
      }
      node = node.parentElement;
    }
    return window.innerHeight;
  }

  /**
   * Anchor the more menu above the trigger when it would overflow below.
   * @returns {void}
   */
  function updateMoreMenuPlacement() {
    const button = moreButtonRef.current;
    const menu = moreMenuRef.current;
    if (!button || !menu) return;

    const buttonRect = button.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const gap = 4;
    const visibleBottom = Math.min(window.innerHeight, getVisibleBottom(button));
    const spaceBelow = visibleBottom - buttonRect.bottom - gap;
    setOpenMoreAbove(spaceBelow < menuHeight);
  }

  useLayoutEffect(() => {
    if (!isMoreOpen) {
      setOpenMoreAbove(false);
      return undefined;
    }
    updateMoreMenuPlacement();
    window.addEventListener("resize", updateMoreMenuPlacement);
    return () => window.removeEventListener("resize", updateMoreMenuPlacement);
  }, [isMoreOpen, isOwnMessage]);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const wrap = bar?.parentElement;
    if (!(wrap instanceof HTMLElement)) return undefined;

    /**
     * Overlay when the bubble leaves too little room beside it for the icon row.
     * Wide main-page panes and short dock messages stay beside the pill.
     * @returns {void}
     */
    function measure() {
      const bubbleRect = wrap.getBoundingClientRect();
      const clip = getHorizontalClipRect(wrap);
      const space = isOwnMessage
        ? bubbleRect.left - clip.left
        : clip.right - bubbleRect.right;
      setOverlayOnBubble(space < ACTION_BAR_MIN_SPACE_PX);
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    const clip = getHorizontalClipRect(wrap);
    if (clip.node) observer.observe(clip.node);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isOwnMessage]);

  /**
   * Toggle the more-actions menu.
   * @returns {void}
   */
  function handleMoreToggle() {
    setIsMoreOpen((prev) => !prev);
  }

  /**
   * Copy message text to clipboard.
   * @returns {Promise<void>}
   */
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(message.content || "");
    } catch (err) {
      console.error("Failed to copy message text:", err);
    }
    setIsMoreOpen(false);
  }

  /**
   * Add or replace reaction with a Unicode emoji.
   * @param {string} emoji
   * @returns {Promise<void>}
   */
  async function handleReact(emoji) {
    const trimmed = (emoji || "").trim();
    if (!trimmed || !chatId) return;

    const existing = (message.reactions || []).find((g) => g.reacted_by_me);
    if (existing?.emoji === trimmed && existing.my_reaction_id) {
      try {
        await deleteChatReaction(existing.my_reaction_id);
      } catch (err) {
        console.error("Failed to remove reaction:", err);
      } finally {
        setIsReactionPickerOpen(false);
      }
      return;
    }

    try {
      await createChatReaction({
        message_id: message.id,
        chat_id: chatId,
        user_id: userId,
        reaction: trimmed,
      });
    } catch (err) {
      console.error("Failed to react to message:", err);
    } finally {
      setIsReactionPickerOpen(false);
    }
  }

  /**
   * Soft-delete a message owned by the current user.
   * @returns {Promise<void>}
   */
  async function handleDelete() {
    try {
      const res = await deleteChatMessage(message.id);
      if (res?.success !== false && typeof onMessageDeleted === "function") {
        onMessageDeleted(message.id);
      }
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
    setIsMoreOpen(false);
  }

  if (message.deleted_at) {
    return null;
  }

  const menuTimestamp = formatMenuTimestamp(
    message.created_at || message.timestamp
  );
  const buttonClass = cn(
    iconButtonClass,
    overlayOnBubble ? iconButtonOverlayClass : iconButtonBesideClass
  );
  const iconClass = overlayOnBubble ? overlayIconClass : undefined;

  return (
    <>
      <div
        ref={barRef}
        className={cn(
          "pointer-events-none absolute z-20 flex items-center opacity-0 transition-opacity duration-150 group-hover/message:pointer-events-auto group-hover/message:opacity-100 group-focus-within/message:pointer-events-auto group-focus-within/message:opacity-100",
          overlayOnBubble
            ? "top-0 right-2 -translate-y-1/2 gap-0 rounded-full border border-ui-border bg-primary px-px shadow-sm"
            : cn(
                "top-1/2 -translate-y-1/2 gap-0.5",
                isOwnMessage ? "right-full pr-1.5" : "left-full pl-1.5"
              )
        )}
      >
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            className={buttonClass}
            aria-label="Add reaction"
            onClick={() => setIsReactionPickerOpen((prev) => !prev)}
          >
            <IconSmile className={iconClass} />
          </button>
          {isReactionPickerOpen && (
            <div className="absolute bottom-full left-1/2 z-30 mb-1 flex -translate-x-1/2 items-center gap-0.5 rounded-md border border-ui-border bg-ui-card px-1.5 py-1 shadow-md">
              {commonReactions.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded-sm px-1.5 py-0.5 text-base text-ui-foreground hover:bg-ui-muted"
                  onClick={() => handleReact(emoji)}
                >
                  {emoji}
                </button>
              ))}
              <button
                type="button"
                className="ml-0.5 inline-flex h-7 w-7 items-center justify-center rounded-sm text-base font-medium text-text-muted hover:bg-ui-muted hover:text-text-primary"
                aria-label="More emojis"
                title="Search all emojis"
                onClick={() => {
                  setIsReactionPickerOpen(false);
                  setIsEmojiModalOpen(true);
                }}
              >
                +
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className={buttonClass}
          aria-label="Reply"
          title="Reply"
          onClick={() => onReply?.(message)}
        >
          <IconReply className={iconClass} />
        </button>

        <div className="relative" ref={moreWrapRef}>
          <button
            ref={moreButtonRef}
            type="button"
            className={buttonClass}
            aria-label="More actions"
            aria-expanded={isMoreOpen}
            onClick={handleMoreToggle}
          >
            <IconMoreVertical className={iconClass} />
          </button>
          {isMoreOpen && (
            <div
              ref={moreMenuRef}
              className={cn(
                "absolute z-30 min-w-[9rem] overflow-hidden rounded-md border border-ui-border bg-ui-card text-sm shadow-md",
                isOwnMessage ? "right-0" : "left-0",
                openMoreAbove ? "bottom-full mb-1" : "top-full mt-1"
              )}
            >
              {menuTimestamp ? (
                <div className="whitespace-nowrap border-b border-ui-border bg-ui-muted px-3 py-2 text-xs text-ui-muted-foreground">
                  {menuTimestamp}
                </div>
              ) : null}
              <div className="py-1">
                <button
                  type="button"
                  className="flex w-full items-center px-3 py-2 text-left text-ui-foreground hover:bg-ui-muted"
                  onClick={handleCopy}
                >
                  Copy
                </button>
                {isOwnMessage && (
                  <button
                    type="button"
                    className="flex w-full items-center px-3 py-2 text-left text-delete hover:bg-highlight"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <EmojiPickerModal
        open={isEmojiModalOpen}
        onOpenChange={setIsEmojiModalOpen}
        onSelect={handleReact}
      />
    </>
  );
}

export default MessageActions;
